/**
 * Centralized test-id environment policy.
 *
 * Determines whether data-testid attributes should be rendered.
 * Locators are stripped in production, demo, and prod environments, and also
 * when the environment is unknown (e.g. no VUE_APP_ENV/NODE_ENV available).
 * In all other known, non-production environments they are rendered.
 *
 * The policy is evaluated once at module load time and cached.
 *
 * Environment detection order:
 *   1. VUE_APP_ENV (set by Vue CLI / webpack)
 *   2. NODE_ENV (fallback)
 *
 * To opt-in locally when env vars are missing, set VUE_APP_TESTID_ENABLED=true.
 *
 * NOTE: `process` is read defensively. This module is bundled by both Vue CLI
 * (standalone apps, where `process` is polyfilled) and the Symfony monolith's
 * Webpack Encore (where `process` is NOT defined and would otherwise throw
 * `ReferenceError: process is not defined` at module load). When `process` is
 * absent, the environment is treated as unknown and locators are disabled so
 * test hooks never ship to production bundles.
 */

const penv = (typeof process !== 'undefined' && process.env) ? process.env : {};
const env = (penv.VUE_APP_ENV || penv.NODE_ENV || '').toLowerCase();
const explicitEnable = (penv.VUE_APP_TESTID_ENABLED || '').toLowerCase() === 'true';

/**
 * Environments where test locators should be disabled (stripped from bundle).
 * @type {string[]}
 */
const TESTID_DISABLED_ENVS = ['production', 'prod', 'demo'];

/**
 * Whether test locators should be rendered in the current environment.
 *
 * An unknown/empty environment (e.g. when built without VUE_APP_ENV/NODE_ENV,
 * as in the Symfony monolith's Encore build) is treated as disabled so that
 * data-testid hooks are never shipped to production.
 *
 * @type {boolean}
 */
export const isTestIdEnabled = explicitEnable || (env !== '' && !TESTID_DISABLED_ENVS.includes(env));

/**
 * Returns a data-testid attribute object for use with v-bind, or an empty object
 * if locators are disabled.
 *
 * @param {string} testId - The test identifier value
 * @returns {Object} - { 'data-testid': testId } or {}
 */
export function testIdAttr(testId) {
  if (!isTestIdEnabled || !testId) {
    return {};
  }
  return { 'data-testid': testId };
}

/**
 * Returns derived testid attributes for form field primitives.
 * Given a base testid, produces { field, input, error } attribute objects.
 *
 * @param {string} base - The base test identifier (e.g., 'login-email')
 * @returns {{ field: Object, input: Object, error: Object }}
 */
export function testIdFieldAttrs(base) {
  if (!isTestIdEnabled || !base) {
    return { field: {}, input: {}, error: {} };
  }
  return {
    field: { 'data-testid': `${base}-field` },
    input: { 'data-testid': `${base}-input` },
    error: { 'data-testid': `${base}-error` },
  };
}

/**
 * Returns a data-testid-key attribute object for use with v-bind on repeated items,
 * or an empty object if locators are disabled.
 *
 * Prefer using the object form of v-testid="{ id, key }" instead of this helper
 * when the element also needs data-testid. This helper is for cases where the key
 * needs to be applied separately (e.g., when testid comes from a component prop).
 *
 * @param {string|number} key - The key value for disambiguation
 * @returns {Object} - { 'data-testid-key': String(key) } or {}
 */
export function testIdKeyAttr(key) {
  if (!isTestIdEnabled || key == null || key === '') {
    return {};
  }
  return { 'data-testid-key': String(key) };
}
